import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { InputControl } from "formik-chakra-ui";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { createCriteriaSet } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import { z } from "zod";

type CriteriaSetFormValues = {
  name: string;
  key: string;
};

const duplicateKeyMessage = "This key already exists";

function criteriaSetFormSchema(existingKeys: readonly string[]) {
  const keys = new Set(existingKeys.map((key) => key.trim()));
  return z.object({
    name: z.string().trim().min(1, "This field is required"),
    key: z
      .string()
      .trim()
      .min(1, "This field is required")
      .refine((key) => !keys.has(key), duplicateKeyMessage),
  });
}

export function AddCriteriaSetLink({
  enrollmentPeriodId,
  hasCriteriaSets,
  existingKeys,
}: {
  enrollmentPeriodId: string;
  hasCriteriaSets: boolean;
  existingKeys: readonly string[];
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formKey, setFormKey] = React.useState(0); // forces the form to remount each time the modal opens

  const open = () => {
    setFormKey((key) => key + 1);
    onOpen();
  };

  return (
    <>
      <Text fontSize="sm">
        {hasCriteriaSets ? (
          <>
            Can't find criteria set?{" "}
            <CriteriaSetLink onClick={open}>Create a set</CriteriaSetLink>
          </>
        ) : (
          <>
            Get started by{" "}
            <CriteriaSetLink onClick={open}>
              creating a criteria set
            </CriteriaSetLink>
            .
          </>
        )}
      </Text>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <AddCriteriaSetForm
          key={formKey}
          enrollmentPeriodId={enrollmentPeriodId}
          existingKeys={existingKeys}
          onCancel={onClose}
          onCreated={onClose}
        />
      </Modal>
    </>
  );
}

function CriteriaSetLink({
  children,
  onClick,
}: {
  children: string;
  onClick: () => void;
}) {
  return (
    <Link
      as="button"
      type="button"
      color="primary.500"
      textDecoration="underline"
      verticalAlign="baseline"
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function AddCriteriaSetForm({
  enrollmentPeriodId,
  existingKeys,
  onCancel,
  onCreated,
}: {
  enrollmentPeriodId: string;
  existingKeys: readonly string[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSubmit = async (values: CriteriaSetFormValues) => {
    setIsSaving(true);
    try {
      await createCriteriaSet({
        throwOnError: true,
        path: { enrollmentPeriodId },
        body: {
          name: values.name.trim(),
          key: values.key.trim(),
        },
      });
      toast({
        id: "create-criteria-set",
        title: "Criteria set created",
      });
      await queryClient.invalidateQueries({
        queryKey: [FSQ.useListCriteriaSetsKey],
      });
      onCreated();
    } catch (err) {
      console.error(err);
      toast.error({
        id: "create-criteria-set",
        title: "Failed to create criteria set",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Formik<CriteriaSetFormValues>
      initialValues={{ name: "", key: "" }}
      onSubmit={handleSubmit}
      validate={(values) =>
        validateWithZod(criteriaSetFormSchema(existingKeys))(values)
      }
      validateOnBlur={false}
    >
      {(formik) => (
        <ModalContent as={Form} noValidate>
          <ModalHeader>Add criteria set</ModalHeader>
          <ModalBody>
            <InputControl
              name="name"
              label="Name"
              marginBottom={4}
              inputProps={{
                autoFocus: true,
                onChange: (event) => {
                  const nextName = event.target.value;
                  const previousName = formik.values.name;
                  const previousKey = formik.values.key;
                  void formik.setFieldValue("name", nextName);
                  if (!isUserGeneratedKey(previousKey, previousName)) {
                    void formik.setFieldValue("key", generateKey(nextName));
                  }
                },
              }}
            />
            <InputControl
              name="key"
              label="Key"
              marginBottom={0}
              {...(formik.errors.key === duplicateKeyMessage
                ? { isInvalid: true }
                : {})}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              type="button"
              variant="ghost"
              colorScheme="gray"
              onClick={onCancel}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSaving}
              loadingText="Creating criteria set"
              isDisabled={!formik.isValid || !formik.dirty}
            >
              Create criteria set
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Formik>
  );
}
